import { TransactionType } from '@repo/database/src/types';
import type { ContractTransaction } from 'ethers';

export const isTransactionType = (type: string): type is TransactionType => {
  return Object.values(TransactionType).includes(type as TransactionType);
};

export const TransactionCurrency = {
  USDT: 'USDT',
  PHNM: 'PHNM',
};

interface BaseTransaction {
  type: TransactionType;
  transactionId: string;
  txCallData: ContractTransaction;
}

const _genericTransactionTypes = [
  TransactionType.PAY_RAKE_SHARE,
  TransactionType.PAY_RAKE_SHARE_BATCH,
  TransactionType.START_DISTRIBUTING_NET_RAKE,
  TransactionType.START_DISTRIBUTING_AGENT_RAKE,
  TransactionType.GRANT_INTERNAL_TOKEN_DEAL,
  TransactionType.REVOKE_INTERNAL_TOKEN_DEAL,
  TransactionType.START_PHNM_TRANSFER,
  TransactionType.TRANSFER_PHNM_TO_TREASURY,
  TransactionType.DISTRIBUTE_REVENUE,
  TransactionType.START_LEFTOVER_REVENUE_TRANSFER,
  TransactionType.START_PHNM_PRICE_UPDATE,
  TransactionType.START_REVENUE_DISTRIBUTION,
  TransactionType.TRANSFER_LEFTOVER_REVENUE_TO_TREASURY,
  TransactionType.UPDATE_PHNM_PRICE,
  TransactionType.END_REWARDS_DISTRIBUTION,
  TransactionType.PAY_REWARDS,
  TransactionType.START_REWARDS_DISTRIBUTION,
  TransactionType.START_REWARDS_FUNDING,
  TransactionType.DISTRIBUTE_NET_RAKE,
] as const;

export type GenericTransactionType = (typeof _genericTransactionTypes)[number];

export interface GenericTransaction extends BaseTransaction {
  type: GenericTransactionType;
}

const _walletTransferTransactionTypes = [
  TransactionType.PAY_RAKE_SHARE,
  TransactionType.PAY_RAKE_SHARE_BATCH,
  TransactionType.START_DISTRIBUTING_NET_RAKE,
  TransactionType.START_DISTRIBUTING_AGENT_RAKE,
  TransactionType.GRANT_INTERNAL_TOKEN_DEAL,
  TransactionType.REVOKE_INTERNAL_TOKEN_DEAL,
  TransactionType.START_PHNM_TRANSFER,
  TransactionType.TRANSFER_PHNM_TO_TREASURY,
  TransactionType.DISTRIBUTE_REVENUE,
  TransactionType.START_LEFTOVER_REVENUE_TRANSFER,
  TransactionType.START_PHNM_PRICE_UPDATE,
  TransactionType.START_REVENUE_DISTRIBUTION,
  TransactionType.TRANSFER_LEFTOVER_REVENUE_TO_TREASURY,
  TransactionType.UPDATE_PHNM_PRICE,
  TransactionType.END_REWARDS_DISTRIBUTION,
  TransactionType.PAY_REWARDS,
  TransactionType.START_REWARDS_DISTRIBUTION,
  TransactionType.START_REWARDS_FUNDING,
  TransactionType.DISTRIBUTE_NET_RAKE,
  TransactionType.TRANSFER_BACKEND_TO_TICKET,
  TransactionType.TRANSFER_TICKET_TO_BACKEND,
  TransactionType.TRANSFER_TICKET_TO_POOL,
] as const;

export type WalletTransferTransactionType = (typeof _walletTransferTransactionTypes)[number];

export interface WalletTransferTransaction extends BaseTransaction {
  type: WalletTransferTransactionType;
  amount?: number;
  recipient?: string;
}

interface CollectLeaderboardPrizeTransaction extends BaseTransaction {
  type: typeof TransactionType.COLLECT_LEADERBOARD_PRIZE;
  userId: string;
  amount: number;
  contractTableId: number;
  tableId: string;
}

interface TransferPoolToLeaderboardTransaction extends BaseTransaction {
  type: typeof TransactionType.TRANSFER_POOL_TO_LEADERBOARD;
  amount: number;
  recipient: string;
  leaderboardId?: string;
}

interface TransferTournamentToTicketTransaction extends BaseTransaction {
  type: typeof TransactionType.TRANSFER_TOURNAMENT_TO_TICKET;
  tournamentId: string;
  tournamentDollars: number | undefined;
}

export interface LeaderboardPrizePayoutTransaction extends BaseTransaction {
  type: typeof TransactionType.LEADERBOARD_PRIZE_PAYOUT;
  userId: string;
  amount: number;
  recipient: string;
}

export interface LeaveTableTransaction extends BaseTransaction {
  type: typeof TransactionType.LEAVE_TABLE;
  contractTableId: number;
  userId: string;
  recipient: string;
  tableId: string;
}

interface CollectRakeTransaction extends BaseTransaction {
  type: typeof TransactionType.COLLECT_RAKE;
  contractTableId: number;
  amount: number;
  userId?: string;
  tableId?: string;
}

export interface PlayerDepositTransaction extends BaseTransaction {
  type: typeof TransactionType.TRANSFER_POOL_TO_PLAYER;
  userId: string;
  recipient: string;
  amount: string | number;
}

export interface DepositBonusTransaction extends BaseTransaction {
  type: typeof TransactionType.DEPOSIT_BONUS;
  userId: string;
  amount: number;
  recipient: string;
}

interface FundTableTransaction extends BaseTransaction {
  type: typeof TransactionType.FUND_TABLE;
  amount: number;
  contractTableId: number;
}

interface UsdtApproveTransaction extends BaseTransaction {
  type: typeof TransactionType.USDT_APPROVE;
  spender: string;
  amount: number;
}

interface UsdtApproveTicketTransaction extends BaseTransaction {
  type: typeof TransactionType.USDT_APPROVE_TICKET;
  spender: string;
}

interface UsdtTransferTransaction extends BaseTransaction {
  type: typeof TransactionType.USDT_TRANSFER;
  amount: number;
  recipient: string;
}

export interface CreateTableTransaction extends BaseTransaction {
  type: typeof TransactionType.CREATE_TABLE;
}

interface TournamentRefundTransaction extends BaseTransaction {
  type: typeof TransactionType.TOURNAMENT_REFUND;
  tournamentId: string;
  recipient: string;
  addressFrom: string;
  ticketId?: string;
  userId: string;
}

interface TournamentEliminatePlayerTransaction extends BaseTransaction {
  type: typeof TransactionType.TOURNAMENT_ELIMINATE_PLAYER | typeof TransactionType.TOURNAMENT_PRIZE;
  tournamentId: string;
  amount: number;
  userId: string;
  recipient: string;
}

export interface TournamentRegistrationTransaction extends BaseTransaction {
  type: typeof TransactionType.TOURNAMENT_REGISTRATION;
  tournamentId: string;
  amount: number;
  userId: string;
}

export interface TournamentRegistrationWithTicketTransaction extends BaseTransaction {
  type: typeof TransactionType.TOURNAMENT_REGISTRATION_WITH_TICKET;
  tournamentId: string;
  amount: number;
  userId: string;
  ticketId?: string;
}

export interface TournamentUnregistrationTransaction extends BaseTransaction {
  type: typeof TransactionType.TOURNAMENT_UNREGISTRATION;
  tournamentId: string;
  userId: string;
  ticketId?: string;
}

export interface TournamentRakeDistributionTransaction extends BaseTransaction {
  type: typeof TransactionType.TOURNAMENT_RAKE_DISTRIBUTION;
}

const tableTransferTransactionTypes = [
  TransactionType.TABLE_REFUND,
  TransactionType.REBUY_REFUND,
  TransactionType.TOPUP_REFUND,
  TransactionType.RECOVER_FUNDS,
  TransactionType.COLLECT_LEADERBOARD_PRIZE,
] as const;

export type TableTransferTransactionType = (typeof tableTransferTransactionTypes)[number];

export const isTableTransferTransactionType = (type: string): type is TableTransferTransactionType =>
  tableTransferTransactionTypes.includes(type as TableTransferTransactionType);

export interface TableTransferTransaction extends BaseTransaction {
  type: TableTransferTransactionType;
  userId?: string;
  recipient: string;
  contractTableId: number;
  amount: number;
  tableId: string;
}

// Tournament contract generic transaction types
const _tournamentContractTransactionTypes = [
  TransactionType.TOURNAMENT_PAUSE,
  TransactionType.TOURNAMENT_UNPAUSE,
  TransactionType.TOURNAMENT_CLOSE_REGISTRATION,
  TransactionType.TOURNAMENT_START,
  TransactionType.TOURNAMENT_COLLECT_RAKE,
  TransactionType.TOURNAMENT_END,
  TransactionType.TOURNAMENT_FUND_PRIZE_POOL,
  TransactionType.TOURNAMENT_DEPLOY,
] as const;

type TournamentContractTransactionType = (typeof _tournamentContractTransactionTypes)[number];

export interface TournamentContractTransaction extends BaseTransaction {
  type: TournamentContractTransactionType;
  tournamentId: string;
}

export interface TournamentExtractFundsTransaction extends BaseTransaction {
  type: typeof TransactionType.TOURNAMENT_EXTRACT_FUNDS;
  tournamentId: string;
  to: string;
  amount: number;
}

export interface TournamentFundPrizePool extends BaseTransaction {
  type: typeof TransactionType.TOURNAMENT_FUND_PRIZE_POOL;
  tournamentId: string;
  amount: number;
}

export interface TransferLeaderboardToPoolTransaction extends BaseTransaction {
  type: typeof TransactionType.TRANSFER_LEADERBOARD_TO_POOL;
  amount: number;
  recipient: string;
}

export interface TicketWalletFundedTransaction extends BaseTransaction {
  type: typeof TransactionType.TRANSFER_POOL_TO_TICKET;
  amount: number;
  userId: string;
  recipient: string;
  tournamentId?: string;
  tournamentDollars?: number;
}

export interface TransferTicketToPoolTransaction extends BaseTransaction {
  type: typeof TransactionType.TRANSFER_TICKET_TO_POOL;
  amount: number;
  userId: string;
  recipient: string;
  tournamentDollars: number;
}

export interface FundSatelliteTicketPoolTransaction extends BaseTransaction {
  type: typeof TransactionType.FUND_SATELLITE_TICKET_POOL;
  tournamentId: string;
}

export type TransactionData =
  | GenericTransaction
  | WalletTransferTransaction
  | LeaveTableTransaction
  | CollectRakeTransaction
  | PlayerDepositTransaction
  | FundTableTransaction
  | UsdtApproveTransaction
  | UsdtApproveTicketTransaction
  | UsdtTransferTransaction
  | TableTransferTransaction
  | DepositBonusTransaction
  | CollectLeaderboardPrizeTransaction
  | LeaderboardPrizePayoutTransaction
  | CreateTableTransaction
  | TournamentContractTransaction
  | TournamentRefundTransaction
  | TournamentRegistrationTransaction
  | TournamentRegistrationWithTicketTransaction
  | TournamentUnregistrationTransaction
  | TournamentEliminatePlayerTransaction
  | TransferLeaderboardToPoolTransaction
  | TournamentFundPrizePool
  | TournamentExtractFundsTransaction
  | TransferPoolToLeaderboardTransaction
  | TicketWalletFundedTransaction
  | TransferTicketToPoolTransaction
  | TransferTournamentToTicketTransaction
  | TournamentRakeDistributionTransaction
  | FundSatelliteTicketPoolTransaction;
